export type TiptapDocument = Record<string, unknown>;
export type TiptapBlock = Record<string, unknown>;
export type ContentLanguage = 'nl' | 'fr' | 'en';
export type TranslationOrigin = 'automatic' | 'manual' | 'unknown';
export type TranslationReview = {
  sourceHash?: string;
  origin?: TranslationOrigin;
  reviewedHash?: string;
};
export type FieldTranslationMetadata = Partial<Record<ContentLanguage, Record<string, TranslationReview>>>;
export type TranslationState = 'missing' | 'changed' | 'unreviewed' | 'reviewed';
export type TranslationEntry = {
  block: TiptapBlock;
  sourceHash: string;
  origin?: TranslationOrigin;
  reviewedHash?: string;
};
export type TranslationCopy = Record<string, TranslationEntry>;
export type CaseContent = {
  master: TiptapDocument;
  sourceLanguage: ContentLanguage;
  translations?: Partial<Record<ContentLanguage, TranslationCopy>>;
  fieldMetadata?: FieldTranslationMetadata;
};
export type TranslationBlock = {
  id: string;
  block: TiptapBlock;
  text: string;
  sourceHash: string;
};

const translatableNodeTypes: Record<string, true> = { heading: true, paragraph: true };

function plainText(node: unknown): string {
  if (!node || typeof node !== 'object') return '';
  const record = node as Record<string, unknown>;
  if (typeof record.text === 'string') return record.text;
  return Array.isArray(record.content) ? record.content.map(plainText).join('') : '';
}

function stableJson(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (typeof value === 'object') {
    return `{${Object.entries(value as Record<string, unknown>)
      .filter(([, item]) => item !== undefined)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => `${JSON.stringify(key)}:${stableJson(item)}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

function fnv1a(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a-${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export function translationTextHash(text: string): string {
  return fnv1a(text);
}

export function translationPlainText(block: unknown): string {
  return plainText(block);
}

/** Review is valid only for the current source snapshot and current target content. */
export function translationState(
  sourceHash: string,
  targetHash: string,
  hasText: boolean,
  metadata?: TranslationReview
): TranslationState {
  if (!hasText) return 'missing';
  if (metadata?.sourceHash && metadata.sourceHash !== sourceHash) return 'changed';
  return metadata?.sourceHash === sourceHash && metadata.reviewedHash === targetHash ? 'reviewed' : 'unreviewed';
}

export function translationBlockId(block: TiptapBlock): string | undefined {
  const id = (block.attrs as Record<string, unknown> | undefined)?.translationId;
  return typeof id === 'string' && id ? id : undefined;
}

/** A content fingerprint used to detect translations that need review after source edits. */
export function translationSourceHash(block: TiptapBlock): string {
  const attrs = { ...((block.attrs as Record<string, unknown> | undefined) ?? {}) };
  delete attrs.translationId;
  return fnv1a(
    stableJson({
      type: block.type,
      ...(Object.keys(attrs).length ? { attrs } : {}),
      content: block.content ?? [],
    })
  );
}

/** Creates missing IDs and replaces duplicates without changing master content structure. */
export function ensureTranslationIds(document: TiptapDocument): TiptapDocument {
  const normalized = structuredClone(document);
  const seen = new Set<string>();
  const visit = (value: unknown): void => {
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (!value || typeof value !== 'object') return;
    const block = value as TiptapBlock;
    if (translatableNodeTypes[block.type as string]) {
      const id = translationBlockId(block);
      if (!id || seen.has(id))
        block.attrs = { ...(block.attrs as Record<string, unknown>), translationId: crypto.randomUUID() };
      seen.add(translationBlockId(block)!);
    }
    if (Array.isArray(block.content)) visit(block.content);
  };
  visit(normalized);
  return normalized;
}

/** A semantic translation unit: an identified heading, paragraph, or list-item paragraph. */
export function translationBlocks(document: unknown): TranslationBlock[] {
  const blocks: TranslationBlock[] = [];
  const visit = (value: unknown) => {
    if (Array.isArray(value)) return value.forEach(visit);
    if (!value || typeof value !== 'object') return;
    const block = value as TiptapBlock;
    const id = translationBlockId(block);
    if (id && translatableNodeTypes[block.type as string] && plainText(block).trim()) {
      blocks.push({ id, block, text: plainText(block), sourceHash: translationSourceHash(block) });
      return;
    }
    if (Array.isArray(block.content)) visit(block.content);
  };
  visit(document);
  return blocks;
}

export function validTranslationCopy(document: unknown, copy: TranslationCopy): boolean {
  const sourceBlocks = translationBlocks(document);
  const source = new Map(sourceBlocks.map(block => [block.id, block]));
  return (
    source.size === sourceBlocks.length &&
    Object.entries(copy).every(([id, entry]) => {
      const master = source.get(id);
      return Boolean(
        master &&
        entry &&
        typeof entry === 'object' &&
        typeof entry.sourceHash === 'string' &&
        entry.sourceHash &&
        entry.block &&
        typeof entry.block === 'object' &&
        master.block.type === entry.block.type &&
        Array.isArray(entry.block.content)
      );
    })
  );
}

const publicMediaUrlPattern =
  /^\/api\/public\/media\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\?width=(?:480|1000|2000))?$/i;

/** Reject externally hosted image sources before rich content is persisted. */
export function contentUsesOnlyPublicMediaUrls(value: unknown): boolean {
  if (Array.isArray(value)) {
    for (const item of value) {
      if (!contentUsesOnlyPublicMediaUrls(item)) return false;
    }
    return true;
  }
  if (!value || typeof value !== 'object') return true;

  const record = value as Record<string, unknown>;
  for (const key in record) {
    if (!Object.prototype.hasOwnProperty.call(record, key)) continue;
    const item = record[key];
    if (key === 'src' && (typeof item !== 'string' || !publicMediaUrlPattern.test(item))) return false;
    if (!contentUsesOnlyPublicMediaUrls(item)) return false;
  }
  return true;
}
